// FP3-071 — Help Center MVP (Jun 8, 2026).
//
// Static, accordion-based quick-reference guide. No backend, no DB.
// Each top-level section renders as a card; articles inside a section
// render as Radix accordion items.
//
// Copy is provider-friendly, not technical. Avoid implementation
// jargon — describe behavior, not the code path. No PHI ever.

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Rocket,
  CalendarBlank,
  Users,
  Brain,
  ClipboardText,
  Question,
} from "@phosphor-icons/react";
import PageShell from "@/components/layout/PageShell";

// ─── Content model ──────────────────────────────────────────────────
// Each section: { id, title, icon, intro, articles[{ id, title, body }] }
// `body` is an array of paragraphs / bullet-arrays so the renderer can
// keep things scannable without an HTML editor.

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Rocket,
    intro:
      "Quick how-tos for setting up your workspace and bringing students into Frame Practice.",
    articles: [
      {
        id: "start-solo-pilot",
        title: "Start a Solo Pilot Workspace",
        body: [
          "Request Access from the public site — Frame Practice creates a private solo workspace for you, no admin needed.",
          {
            bullets: [
              "Your workspace is yours alone. No one else can see your students or schedule.",
              "Set your role, schools, and availability the first time you sign in.",
              "You can start adding students immediately — pilot access is free during the rollout.",
            ],
          },
          "When you outgrow solo (assistants, multiple providers, district rollout), Frame Practice can convert your workspace to a team or district plan without losing your data.",
        ],
      },
      {
        id: "add-import-students",
        title: "Add or Import Students",
        body: [
          "Two paths, both available to solo and school-team providers:",
          {
            bullets: [
              "Add Student modal — quickly create one student at a time with name, grade, campus, attendance pattern, and Service Status.",
              "CSV Import — paste your SIS export to bring 20–80 students in one pass. Frame Practice maps common columns automatically.",
            ],
          },
          "After adding, open the student profile and fill out Service Demographics so the Schedule Wizard has what it needs.",
        ],
      },
      {
        id: "set-service-status",
        title: "Set Service Status",
        body: [
          "Service Status lives in Student Profile → Service Demographics → Scheduling Profile. It's the single source of truth for whether a student is currently in active therapy.",
          {
            bullets: [
              "IEP — students receiving services under an IEP. Included in scheduling.",
              "RTI — students receiving direct RTI services. Included in scheduling.",
              "Monitoring — watchlist; not scheduled. Use the Provider Workboard for early notes.",
              "Graduated — exited the program. Not scheduled. History preserved.",
              "Archived — terminal flag for any other inactive reason. Not scheduled. History preserved.",
            ],
          },
          "Service Status is separate from Archive and Delete. Changing the status never removes goals, notes, sessions, documents, or history.",
        ],
      },
    ],
  },
  {
    id: "scheduling",
    title: "Scheduling",
    icon: CalendarBlank,
    intro:
      "How the Schedule Wizard, Builder, Health, and Service Fidelity work together.",
    articles: [
      {
        id: "wizard-overview",
        title: "How the Schedule Wizard Works",
        body: [
          "The Schedule Wizard generates a draft weekly schedule for the students you select.",
          {
            bullets: [
              "It only schedules students in Active Therapy (IEP + RTI).",
              "It respects school attendance windows, grade rules, your availability, and any personal blocked time you've set.",
              "It runs a pre-flight check first and warns you about students missing a service commitment, school, grade, attendance pattern, or provider assignment.",
              "It never overwrites your existing schedule — it builds a draft you review before applying.",
            ],
          },
          "Think of it as a starting point. Review the draft, adjust in the Builder, then commit.",
        ],
      },
      {
        id: "active-therapy",
        title: "Active Therapy: IEP + RTI",
        body: [
          "Active Therapy is the set of students who should be on your schedule right now:",
          {
            bullets: [
              "IEP students — included.",
              "RTI students — included.",
              "Monitoring, Graduated, Archived, and Deleted students — not included.",
            ],
          },
          "You can see exactly who counts as Active Therapy from Caseload → Status filter → \"Active Therapy.\" The same set appears in the Wizard, in Health validations, and in Service Fidelity.",
          "If a student needs to move in or out of Active Therapy, change their Service Status — the rest of the platform updates automatically on the next refresh.",
        ],
      },
      {
        id: "school-vs-provider-rules",
        title: "School Rules vs Provider Rules",
        body: [
          "Frame Practice keeps the two kinds of constraints separate so each one is easy to update.",
          {
            bullets: [
              "School Rules define student availability: attendance window (Full Day / AM / PM), grade schedules, lunch, specials, early-release days, and the school-day window.",
              "Provider Rules define your availability: working days, working hours, recurring personal blocks (testing weeks, all-day admin, every-Nth-week patterns), and one-off blocked time.",
            ],
          },
          "The Wizard respects BOTH at the same time — a session is only proposed when the student is available AND you are available.",
        ],
      },
      {
        id: "closures",
        title: "How Frame Practice Handles Closures",
        body: [
          "A school closure or break doesn't break your recurring schedule.",
          {
            bullets: [
              "Your recurring blocks stay in place — they're the template.",
              "Frame Practice materializes individual sessions for each calendar week. Sessions that fall on a closure / break / non-instructional day are skipped automatically.",
              "Health looks at the recurring schedule — it tells you whether your weekly template covers each student's service commitment.",
              "Service Fidelity looks at actual delivered vs. expected sessions after closures are applied — so a holiday week won't make a student look behind.",
            ],
          },
        ],
      },
      {
        id: "health-vs-fidelity",
        title: "Why Health and Fidelity May Look Different",
        body: [
          "Health and Service Fidelity answer two different questions, so they often show different numbers.",
          {
            bullets: [
              "Health: does my recurring schedule satisfy each student's service commitment, in theory? It uses recurring-block math and ignores closures.",
              "Service Fidelity: how many sessions have I actually delivered vs. what was expected, accounting for closures and make-ups?",
            ],
          },
          "Health is your weekly template check. Fidelity is your real-world delivery check. Use both — they're complementary.",
        ],
      },
    ],
  },
  {
    id: "assistants",
    title: "Assistants / SLPAs",
    icon: Users,
    intro:
      "How supervising providers and assistants work together in Frame Practice.",
    articles: [
      {
        id: "invite-assistant",
        title: "Invite an Assistant",
        body: [
          "Send an invite from your workspace — the assistant signs in with their own account and is automatically linked to you as their supervisor.",
          {
            bullets: [
              "Assistants only see students you've explicitly assigned to them.",
              "Assistants get their own caseload view, their own schedule, and their own Workboard.",
              "They never automatically inherit your full caseload or your Workboard.",
            ],
          },
        ],
      },
      {
        id: "assign-students",
        title: "Assign Students to an Assistant",
        body: [
          "Open the student profile → Team tab → add the assistant as a team member.",
          {
            bullets: [
              "An assistant must share your organization and have you set as their supervising provider.",
              "Frame Practice checks this every time the team list is updated, so you can't accidentally cross orgs.",
              "Reassigning is reversible — remove the assistant from the team list to revoke access.",
            ],
          },
        ],
      },
      {
        id: "view-assistant-schedule",
        title: "View an Assistant's Schedule",
        body: [
          "From the Caseload page, open the My Assistants card and click Schedule next to the assistant.",
          {
            bullets: [
              "You see the assistant's calendar in read-only \"supervision\" mode.",
              "Sessions stay attributed to the assistant — viewing doesn't reassign anything.",
              "Personal/blocked-out time on the assistant's calendar is visible but not editable.",
            ],
          },
        ],
      },
      {
        id: "message-assistant",
        title: "Message an Assistant",
        body: [
          "Use the Message button on the My Assistants card or from any goal-context Message link.",
          {
            bullets: [
              "Direct messages land in the assistant's inbox under \"DMs.\"",
              "Goal-context messages are auto-routed to the right recipient (the assistant when you're supervising goal evidence).",
              "Read receipts work both directions.",
            ],
          },
        ],
      },
      {
        id: "supervisor-lens",
        title: "Use Supervisor Lens",
        body: [
          "Supervisor Lens is your single page for everyone you supervise.",
          {
            bullets: [
              "Lists every assistant under your supervision plus the students they serve.",
              "Shows recent goal evidence from supervised students at a glance.",
              "Lets you jump straight to the student's Goals / Data / Notes tabs without leaving the page.",
              "Data shown is read-only — Supervisor Lens never mutates assistant work.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "assessments-goals-ai",
    title: "Assessments, Goals, and AI Support",
    icon: Brain,
    intro:
      "How to use the Assessments tab, the Goal Plan tab, and AI suggestions while keeping your clinical judgment in the driver's seat.",
    articles: [
      {
        id: "uploading-assessments",
        title: "Uploading and Reviewing Assessments",
        body: [
          "Open a student → Assessments tab → upload evaluation-related documents.",
          {
            bullets: [
              "PDFs, text-based reports, and most common eval document formats are supported.",
              "Image-only PDFs, restricted/encrypted files, or scans with poor text recognition may not be usable by AI features.",
              "Uploaded files live with the student and are visible only to providers on that student's team.",
            ],
          },
          "The Assessments tab is a place to store and review eval materials — it doesn't replace your interpretation or clinical judgment.",
        ],
      },
      {
        id: "ai-goal-support",
        title: "Using AI Goal Support",
        body: [
          "AI Goal Support can suggest draft goal ideas based on information you select — typically pulled from assessments or the Goal Plan.",
          {
            bullets: [
              "AI output is a draft, never a final IEP goal.",
              "Always review for accuracy, measurability, educational relevance, and student fit.",
              "Edit the wording. Edit the target. Edit the timeline. Make it yours.",
              "Do not paste AI output directly into an official document without provider review.",
            ],
          },
          "AI is a writing partner. The provider is the decision-maker.",
        ],
      },
      {
        id: "goal-plan-tab",
        title: "Using the Goal Plan Tab",
        body: [
          "The Goal Plan tab is a thinking space — a structured place to organize what a student might need long-term.",
          {
            bullets: [
              "Capture long-term development areas (e.g., articulation, expressive language, social pragmatics).",
              "List suggested focus areas under each long-term area.",
              "Draft possible goal directions before you commit to writing them as official goals.",
            ],
          },
          "When you're ready, promote a Goal Plan target into an active goal. Goal Plan content guides your thinking — it doesn't replace your final decision.",
        ],
      },
      {
        id: "assessment-to-goals",
        title: "Turning Assessment Information into Goal Ideas",
        body: [
          "Frame Practice can help bridge the gap between an evaluation report and a written goal.",
          {
            bullets: [
              "Open Goal Plan → select the assessment(s) you want to draw from.",
              "Frame Practice surfaces relevant findings (areas of need, baseline language) for your reference.",
              "AI Goal Support can then propose draft wording for one of those areas.",
              "You review, revise, and decide what becomes a real goal.",
            ],
          },
        ],
      },
      {
        id: "provider-judgment",
        title: "Keeping Provider Judgment in the Loop",
        body: [
          "Frame Practice can organize data and suggest wording. The provider is always responsible for the final product.",
          {
            bullets: [
              "Review AI-generated content for accuracy and appropriateness before using it.",
              "Confirm goals are measurable, observable, and aligned with the student's actual needs.",
              "Confirm any cited assessment data matches the source document.",
              "Edit anything that doesn't fit the student or doesn't read the way you write.",
            ],
          },
          "AI is a productivity tool, not a clinical authority.",
        ],
      },
    ],
  },
  {
    id: "workboard",
    title: "Provider Workboard",
    icon: ClipboardText,
    intro: "What the Workboard is — and what it isn't.",
    articles: [
      {
        id: "workboard-for",
        title: "What the Provider Workboard Is For",
        body: [
          "The Workboard is your scratch space for students or tasks that are NOT yet on your active caseload.",
          {
            bullets: [
              "Teacher referrals you're tracking.",
              "Screening follow-ups.",
              "Students you're watching for possible speech/language concerns.",
              "Students waiting on parent or teacher input.",
              "General provider task notes related to a student or potential student.",
            ],
          },
          "Use Source, Status, and Priority chips to keep it skimmable. Open the row to add a longer note.",
        ],
      },
      {
        id: "workboard-vs-students",
        title: "Workboard Rows vs Official Student Records",
        body: [
          "A Workboard row is informal. An official student record is the source of truth for scheduling, goals, and reporting.",
          {
            bullets: [
              "Workboard rows do not create official student records.",
              "Workboard rows do not appear in Caseload, Schedule Wizard, Health, Goals, Reports, or Service Fidelity.",
              "Workboard rows do not require a service commitment, school assignment, or attendance pattern.",
              "Deleting a Workboard row only removes it from the Workboard — it never touches an official student record.",
            ],
          },
          "When a Workboard student becomes active in service, create them as an official student record (Add Student or CSV Import) and set their Service Status to IEP or RTI.",
        ],
      },
    ],
  },
];

// ─── Renderers ──────────────────────────────────────────────────────

function ArticleBody({ body }) {
  return (
    <div className="space-y-3 text-[13px] leading-relaxed text-[#3F4A4A] max-w-[72ch]">
      {body.map((block, i) => {
        if (typeof block === "string") {
          return <p key={i}>{block}</p>;
        }
        if (block && Array.isArray(block.bullets)) {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1.5 marker:text-[#9B9890]">
              {block.bullets.map((b, j) => (
                <li key={j}>{b}</li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </div>
  );
}

function HelpSection({ section, openIds }) {
  const Icon = section.icon;
  return (
    <Card
      data-testid={`help-section-${section.id}`}
      id={`help-section-${section.id}`}
      className="border-[#E5E3DB] bg-white"
    >
      <CardHeader className="pb-2 flex flex-row items-start gap-2.5 space-y-0">
        <div className="mt-0.5 p-1.5 rounded-md bg-[#EFEDE5] text-[#1E3F33]">
          <Icon size={18} weight="duotone" />
        </div>
        <div>
          <CardTitle className="text-[15px] font-semibold text-[#1E3F33] heading-font tracking-tight">
            {section.title}
          </CardTitle>
          <p className="text-[12px] text-[#6E6A60] mt-0.5 leading-snug">
            {section.intro}
          </p>
        </div>
      </CardHeader>
      <CardContent className="pt-1">
        <Accordion
          type="multiple"
          // FP3-072 — Auto-open articles whose id appears in the URL
          // hash (e.g. `/help#set-service-status`). Plain string array
          // because `Accordion type="multiple"` requires defaultValue
          // to be an array. We bind via `defaultValue` (uncontrolled)
          // so user can still toggle freely after the initial open.
          defaultValue={openIds}
          className="divide-y divide-[#EEEBE3]"
          data-testid={`help-accordion-${section.id}`}
        >
          {section.articles.map((a) => (
            <AccordionItem
              key={a.id}
              value={a.id}
              // FP3-072 — Stable DOM id for hash deep-linking.
              id={a.id}
              data-testid={`help-article-${a.id}`}
              className="border-b-0 scroll-mt-20"
            >
              <AccordionTrigger
                data-testid={`help-article-${a.id}-trigger`}
                className="text-[13px] font-medium text-[#1F2937] py-3 hover:no-underline hover:text-[#1E3F33]"
              >
                {a.title}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`help-article-${a.id}-content`}
                className="pb-4 pt-1"
              >
                <ArticleBody body={a.body} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
}

export default function HelpCenter() {
  // FP3-072 — Hash-based deep linking. Read `window.location.hash` on
  // mount + on hashchange so we auto-open the matching accordion
  // article AND scroll it into view. Multiple sections each render
  // their own Accordion; we compute per-section openIds and pass
  // them in via defaultValue.
  const [hashTarget, setHashTarget] = useState(() =>
    (typeof window !== "undefined" ? (window.location.hash || "").replace(/^#/, "") : "")
  );
  useEffect(() => {
    const onHash = () => setHashTarget((window.location.hash || "").replace(/^#/, ""));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    if (!hashTarget) return;
    // Defer scroll to next tick so the accordion has rendered its
    // expanded content first.
    const t = setTimeout(() => {
      const el = document.getElementById(hashTarget);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [hashTarget]);

  return (
    <PageShell testId="help-center-page" variant="compact" className="animate-none">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <div className="mt-1 p-2 rounded-lg bg-[#EFEDE5] text-[#1E3F33]">
          <Question size={22} weight="duotone" />
        </div>
        <div>
          <h1 className="text-[26px] font-semibold text-[#1E3F33] heading-font tracking-tight">
            Help Center
          </h1>
          <p className="text-[13px] text-[#6E6A60] mt-1 max-w-2xl leading-relaxed">
            Quick guides for setting up students, scheduling services, developing
            goals, and using Frame Practice.
          </p>
        </div>
      </div>

      {/* Sections — one column on narrow widths; two-column grid on
          xl+ (≥1280px) where sections are naturally side-by-side
          without cramping accordion triggers. Inner ArticleBody
          retains a 72ch readable line-length so open article text
          stays comfortable to read. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {SECTIONS.map((s) => {
          // FP3-072 — Pre-compute which articles to auto-open per
          // section based on the URL hash. Only one article will
          // match (article ids are globally unique), but we pass
          // an array since Accordion type="multiple" requires it.
          const openIds = hashTarget && s.articles.some((a) => a.id === hashTarget)
            ? [hashTarget]
            : undefined;
          return <HelpSection key={s.id} section={s} openIds={openIds} />;
        })}
      </div>

      {/* Footer / tone reminder */}
      <p
        data-testid="help-footer-note"
        className="text-[11.5px] text-[#9B9890] italic text-center mt-8 mb-2"
      >
        Frame Practice helps you organize information and draft language. You remain
        responsible for clinical and educational decisions.
      </p>
    </PageShell>
  );
}
